import { useQuery } from '@tanstack/react-query';
import {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { CurrencyChangeModal } from '../../components/CurrencyChangeModal';
import { COUNTRY_INFO_MAP, DEFAULT_COUNTRY_INFO } from '../../constants/country-info-map';
import { DEFAULT_REGION } from '../../constants/currency';
import { CURRENCY_SYMBOL_MAP } from '../../constants/currency-symbol-map';
import { WebshopPriceTiersApi } from '../../services/webshop-regions-api';
import type { CountryInfoWithCurrency } from '../../types/country-info';
import { CurrencyType } from '../../types/offers';
import { useGeolocation } from '../geolocation';
import { useModal } from '../modal-context';

export const SELECTED_CURRENCY_STORAGE_KEY = 'selectedCurrency';
export const SELECTED_REGION_STORAGE_KEY = 'selectedRegion';

type CurrenciesContextProps = {
  selectedCurrency: CurrencyType;
  setSelectedCurrency: (currency: CurrencyType) => void;
  selectedRegion: string;
  setSelectedRegion: (regionCode: string) => void;
  countries: CountryInfoWithCurrency[];
  openRegionChangeModal: () => Promise<void>;
  selectedCountryInfo: CountryInfoWithCurrency;
};

export const CurrenciesContext = createContext<CurrenciesContextProps | undefined>(undefined);

export const CurrenciesProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams();
  const { data: geolocationData } = useGeolocation();

  const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType>(
    searchParams.get('currency') as CurrencyType
    ?? localStorage.getItem(SELECTED_CURRENCY_STORAGE_KEY) as CurrencyType
    ?? 'POWER',
  );

  const [selectedRegion, setSelectedRegion] = useState<string | null>(
    localStorage.getItem(SELECTED_REGION_STORAGE_KEY),
  );
  const safeSelectedRegion = selectedRegion ?? DEFAULT_REGION;

  const { openModal } = useModal();

  const { data: availableRegionsWithCurrencies } = useQuery({
    queryKey: ['webshop', 'pricesheet'],
    queryFn: WebshopPriceTiersApi.getPriceTiers,
    select: (data) => data.currencies,
    refetchInterval: 60000,
  });

  const { countriesMap, countries } = useMemo(
    (): {
      countriesMap: Record<string, CountryInfoWithCurrency>;
      countries: CountryInfoWithCurrency[]
    } => {
      const map = Object.entries(availableRegionsWithCurrencies ?? {})
        .reduce((acc, [country, currency]) => ({
          ...acc,
          [country]: {
            ...COUNTRY_INFO_MAP[country],
            currencySymbol: CURRENCY_SYMBOL_MAP[currency],
            currency,
          },
        }), {});

      return {
        countriesMap: map,
        countries: Object.values(map),
      };
    },
    [availableRegionsWithCurrencies],
  );

  const selectedCountryInfo = useMemo(
    () => countriesMap[safeSelectedRegion] ?? { ...DEFAULT_COUNTRY_INFO, currency: 'USD', currencySymbol: '$' },
    [safeSelectedRegion, countriesMap],
  );

  const openRegionChangeModal = useCallback(
    async () => {
      const newValue = await openModal({
        Component: CurrencyChangeModal,
        props: { selectedRegion: safeSelectedRegion, countries },
      });

      if (newValue) {
        setSelectedRegion(newValue);
      }
    },
    [safeSelectedRegion, countries, openModal],
  );

  useEffect(() => {
    localStorage.setItem(SELECTED_CURRENCY_STORAGE_KEY, selectedCurrency);
  }, [selectedCurrency]);

  useEffect(() => {
    localStorage.setItem(SELECTED_REGION_STORAGE_KEY, selectedRegion ?? '');
  }, [selectedRegion]);

  useEffect(() => {
    if (!selectedCountryInfo) {
      setSelectedRegion(DEFAULT_REGION);
    }
  }, [selectedCountryInfo]);

  useEffect(() => {
    if (selectedRegion || !geolocationData || !COUNTRY_INFO_MAP[geolocationData.country_code]) {
      return;
    }

    setSelectedRegion(geolocationData.country_code);
  }, [geolocationData]);

  const providerValue = useMemo(
    () => ({
      selectedCurrency,
      setSelectedCurrency,
      selectedRegion: safeSelectedRegion,
      setSelectedRegion,
      countries,
      openRegionChangeModal,
      selectedCountryInfo: selectedCountryInfo ?? DEFAULT_COUNTRY_INFO,
    }),
    [selectedRegion, selectedCurrency, countries, openRegionChangeModal, selectedCountryInfo],
  );

  return (
    <CurrenciesContext.Provider value={providerValue}>
      {children}
    </CurrenciesContext.Provider>
  );
};

export const useCurrencies = () => {
  const context = useContext(CurrenciesContext);
  if (!context) {
    throw new Error('useCurrencies must be used within a CurrencyChangeProvider');
  }
  return context;
};
